import { useLayoutEffect, useState } from 'react';
import { getValue, setValue } from '@src/common/storage';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// Floor the seed at 5 so the pill never shows "0 pessoas" or a near-empty number,
// which would defeat the social-proof intent on first visit.
const INITIAL_COUNT_RANGE = { min: 5, max: 50 } as const;
const DAILY_INCREMENT_RANGE = { min: 0, max: 20 } as const;
const INCREMENT_PROBABILITY = 0.3;
const COOKIE_NAMESPACE = 'trending-reservations';
const COOKIE_TTL_DAYS = 30;

type Rng = () => number;
type TrendingReservationsSnapshot = { count: number; updatedAt: number };

function randomBetween(rng: Rng, range: { min: number; max: number }): number {
  return Math.floor(rng() * (range.max - range.min + 1)) + range.min;
}

function pickInitialCount(rng: Rng): number {
  return randomBetween(rng, INITIAL_COUNT_RANGE);
}

function pickDailyIncrement(rng: Rng): number {
  return randomBetween(rng, DAILY_INCREMENT_RANGE);
}

function seedFreshSnapshot(now: number, rng: Rng): TrendingReservationsSnapshot {
  return { count: pickInitialCount(rng), updatedAt: now };
}

function rollWithinSameDay(
  previous: TrendingReservationsSnapshot,
  now: number,
  rng: Rng,
): TrendingReservationsSnapshot {
  const shouldIncrement = rng() < INCREMENT_PROBABILITY;
  const count = shouldIncrement ? previous.count + pickDailyIncrement(rng) : previous.count;
  return { count, updatedAt: now };
}

function resetAndBackfillDays(now: number, daysAway: number, rng: Rng): TrendingReservationsSnapshot {
  let count = pickInitialCount(rng);
  // The first day boundary is the reset itself; each extra day adds one increment.
  for (let day = 1; day < daysAway; day++) {
    count += pickDailyIncrement(rng);
  }
  return { count, updatedAt: now };
}

function advanceTrendingReservations(
  previous: TrendingReservationsSnapshot | null,
  now: number,
  rng: Rng,
): TrendingReservationsSnapshot {
  if (!previous) return seedFreshSnapshot(now, rng);

  // Use elapsed milliseconds rather than calendar-day math so the reset threshold
  // is "24 hours of wall time" regardless of the user's timezone or DST changes.
  const daysAway = Math.floor((now - previous.updatedAt) / ONE_DAY_MS);
  // daysAway < 0 means clock went backwards (user's system time changed); treat as same day.
  if (daysAway <= 0) return rollWithinSameDay(previous, now, rng);
  return resetAndBackfillDays(now, daysAway, rng);
}

function readSnapshot(productId: string): TrendingReservationsSnapshot | null {
  const raw = getValue(COOKIE_NAMESPACE, productId);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.count === 'number' && typeof parsed?.updatedAt === 'number') {
      return parsed as TrendingReservationsSnapshot;
    }
  } catch {
    /* fall through */
  }
  return null;
}

function writeSnapshot(productId: string, snapshot: TrendingReservationsSnapshot): void {
  setValue(COOKIE_NAMESPACE, productId, JSON.stringify(snapshot), COOKIE_TTL_DAYS);
}

export function useTrendingReservations(productId: string | undefined): number | null {
  const [count, setCount] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!productId) return;
    const next = advanceTrendingReservations(readSnapshot(productId), Date.now(), Math.random);
    writeSnapshot(productId, next);
    setCount(next.count);
  }, [productId]);

  return count;
}
